// SHADOW MARKET™ — Patterns Library inside the Academy.
// Route: /academy/patrones
// Uses the same dark, orange-accented aesthetic as AcademyPage.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, GraduationCap, LineChart } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { SimulatorRankCard } from "../components/SimulatorRankCard";
import { PatronCard } from "../components/academy/PatronCard";
import { PATRONES } from "../data/patronesGraficos";
import { filtrarPatronesLang } from "../data/i18n-academy";
import { useLanguage } from "../i18n/LanguageContext";
import { getPatronesUi } from "../i18n/patrones";

const AcademyPatronesPage = () => {
  const [tipo, setTipo] = useState("todos");
  const [nivel, setNivel] = useState("todos");
  const { lang } = useLanguage();
  const ui = getPatronesUi(lang);

  const TIPO_FILTERS = [
    { id: "todos", label: ui.tipo.todos },
    { id: "continuacion", label: ui.tipo.continuacion },
    { id: "reversion", label: ui.tipo.reversion },
    { id: "neutro", label: ui.tipo.neutro },
  ];

  const NIVEL_FILTERS = [
    { id: "todos", label: ui.nivel.todos },
    { id: "basico", label: ui.nivel.basico },
    { id: "intermedio", label: ui.nivel.intermedio },
  ];

  const filtered = useMemo(() => filtrarPatronesLang(lang, tipo, nivel), [lang, tipo, nivel]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      {/* Ambient background — identical to AcademyPage */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,140,0,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(168,85,247,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_70%,rgba(0,229,255,0.10),transparent_55%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      {/* HEADER */}
      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/academy"
            className="group inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="patrones-back-academy"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">
              {ui.back_academy}
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
            <div className="hidden sm:block">
              <h1 className="font-display text-xs font-black uppercase tracking-[0.25em] text-white md:text-sm">
                {ui.page_title}
              </h1>
              <p className="text-[10px] text-white/40">{ui.page_subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <SimulatorRankCard />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 py-8 md:px-10">
        {/* Breadcrumb */}
        <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
          <Link to="/academy" className="transition-colors hover:text-orange-300">
            {ui.breadcrumb_academy}
          </Link>
          <span className="px-2 text-white/25">→</span>
          <span className="text-orange-300">{ui.breadcrumb_patterns}</span>
        </p>

        {/* Title block */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-8"
        >
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/[0.08] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-orange-300 backdrop-blur-sm">
            <LineChart className="h-3 w-3" />
            {ui.eyebrow}
          </div>
          <h2
            className="patrones-hero-title text-3xl font-black tracking-tight text-white md:text-5xl"
            dangerouslySetInnerHTML={{ __html: ui.hero_title_html }}
          />
          <p className="mt-3 max-w-2xl text-sm text-white/55">{ui.hero_sub}</p>
        </motion.div>

        {/* Filters — same pill style as Academy tabs */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
            {ui.filter_type}
          </span>
          {TIPO_FILTERS.map((f) => {
            const active = tipo === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setTipo(f.id)}
                data-testid={`patrones-tipo-${f.id}`}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-all ${
                  active
                    ? "border-orange-400/60 bg-orange-500/15 text-orange-200 shadow-[0_0_20px_rgba(255,140,0,0.3)]"
                    : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/30 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
            {ui.filter_level}
          </span>
          {NIVEL_FILTERS.map((f) => {
            const active = nivel === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setNivel(f.id)}
                data-testid={`patrones-nivel-${f.id}`}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-all ${
                  active
                    ? "border-orange-400/60 bg-orange-500/15 text-orange-200 shadow-[0_0_20px_rgba(255,140,0,0.3)]"
                    : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/30 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Count */}
        <p className="mb-4 text-xs text-white/40" data-testid="patrones-count">
          {ui.showing_template(filtered.length, PATRONES.length)
            .split(String(filtered.length))
            .map((part, i, arr) =>
              i < arr.length - 1 ? (
                <React.Fragment key={i}>
                  {part}
                  <span className="font-bold text-orange-300">{filtered.length}</span>
                </React.Fragment>
              ) : (
                <React.Fragment key={i}>{part}</React.Fragment>
              )
            )}
        </p>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-16 text-center text-sm text-white/40">
            {ui.empty}
          </div>
        ) : (
          <div
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="patrones-grid"
          >
            {filtered.map((p, i) => (
              <PatronCard key={p.id} patron={p} index={i} />
            ))}
          </div>
        )}

        {/* Hint */}
        <div className="mt-12 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
          <GraduationCap className="h-3.5 w-3.5 text-orange-300" />
          {ui.foot_hint}
        </div>
      </main>
    </div>
  );
};

export default AcademyPatronesPage;
